import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'site-modules/shared/components/link/link';
import { getFeaturesSpecsUrlStandard } from 'site-modules/shared/utils/core-link-constructor';
import { getBuildAndPriceLink } from 'site-modules/shared/utils/build-and-price-link-constructor';
import { VEHICLE_SELECT_MODE_MAP } from 'site-modules/shared/constants/price-checker';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';

import './how-to-check.scss';

export function HowToCheck({ onModeChange = null, vehicleParams = null, mode }) {
  const { make, model, year } = vehicleParams || {};
  const isMmyMode = mode === VEHICLE_SELECT_MODE_MAP.MMY;
  const hasMmy = !!(make && model && year) && isMmyMode;

  return (
    <div className="how-to-check text-gray-darker pt-1_5">
      <div className="heading-4 mb-0_5">How to check a dealer price</div>

      <div>Edmunds Price Checker&trade; rates the dealer price quote for a specific car you’re about to purchase.</div>

      <ContentCollapse
        textClosed="Read more"
        textOpen="Show less"
        justify="justify-content-start"
        btnContainerClasses="mt-0_5 px-0_5"
        btnClasses="p-0 text-primary-darker"
        btnSize="lg"
        btnColor="link"
        btnTextClasses="fw-normal"
        showBtnBelow
      >
        <ul className="list-unstyled mb-0 mt-1_5">
          <li className="mb-1_5">
            <span className="fw-bold">Style:</span> You must select a Make, Model and Year before we can populate the
            Style dropdown. Select the version of your next car that has the features you need.
            {hasMmy && (
              <Fragment>
                If you’re not sure, visit our {year.year} {make.name} {model.name}{' '}
                <Link
                  className="text-primary-darker"
                  to={getFeaturesSpecsUrlStandard({ makeSlug: make.slug, modelSlug: model.slug, year: year.year })}
                  data-tracking-id="pc_view_features_specs"
                  data-tracking-value={`${year.year} ${make.name} ${model.name} features & specs comparison`}
                >
                  features & specs comparison
                </Link>{' '}
                page.
              </Fragment>
            )}
          </li>
          <li className="mb-1_5">
            <span className="fw-bold">Dealer price quote:</span> Enter the price that a local dealer has quoted you for
            a specific vehicle including installed optional equipment, but excluding taxes and fees. Make sure the
            vehicle is both the Style you want, and has the{' '}
            {hasMmy ? (
              <Link
                className="text-primary-darker"
                to={getBuildAndPriceLink({ make: make.slug, model: model.slug, year: year.year })}
                data-tracking-id="configure_vehicle"
              >
                installed optional equipment
              </Link>
            ) : (
              'installed optional equipment'
            )}{' '}
            you want.
          </li>
          <li className="mb-1_5">
            <span className="fw-bold">MSRP from quote:</span> This is the Manufacturer’s Suggested Retail Price for the
            same vehicle as your dealer price quote. Enter the MSRP price including installed optional equipment, but
            excluding taxes and fees. If you have the VIN number for the vehicle, you can use our{' '}
            {onModeChange && isMmyMode ? (
              <button
                className="vin-btn border-0 p-0 text-primary-darker"
                onClick={onModeChange}
                data-value={VEHICLE_SELECT_MODE_MAP.VIN}
                data-tracking-id="pc_vin_in_text"
                data-tracking-value="VIN"
              >
                VIN form
              </button>
            ) : (
              'VIN form'
            )}{' '}
            instead, and we’ll populate the MSRP for you.
          </li>
          <li>
            <span className="fw-bold">Rate this dealer offer:</span> Hit this button and our chart will populate with
            the range of prices that shoppers in your area have been paying for the same Style.
          </li>
        </ul>
      </ContentCollapse>
    </div>
  );
}

HowToCheck.propTypes = {
  onModeChange: PropTypes.func,
  vehicleParams: PropTypes.shape({
    make: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    model: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    year: PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  mode: PropTypes.string.isRequired,
};
